import { InfoIcon } from 'lucide-react';
import React from 'react';

import { PlexTracReporting } from '@/components/reporting/PlexTracReporting';
import { Tooltip } from '@/components/Tooltip';
import { ReportSettingsSectionProps } from '@/sections/settings/types';

/**
 * Report Settings Section Component
 *
 * Displays PlexTrac integration and report export configuration.
 * This is a simple wrapper component that provides consistent
 * styling and structure around the PlexTracReporting component.
 *
 * @example
 * ```tsx
 * <ReportSettingsSection
 *   customerEmail="user@example.com"
 *   isPraetorianUser={true}
 * />
 * ```
 *
 * @example Read-only view
 * ```tsx
 * <ReportSettingsSection
 *   customerEmail="current@user.com"
 *   isPraetorianUser={false}
 *   showTitle={false}
 * />
 * ```
 */
export const ReportSettingsSection: React.FC<ReportSettingsSectionProps> = ({
  customerEmail,
  isPraetorianUser,
  isLoading,
  title = 'Report Settings',
  showTitle = true,
  className = '',
}) => {
  return (
    <div
      className={`border-chariot-stroke bg-chariot-background-primary rounded-lg border ${className}`}
    >
      <div className="p-6">
        {/* Section Header */}
        {showTitle && (
          <div className="mb-4 flex items-center gap-2">
            <h3 className="text-xl font-bold">{title}</h3>
            <Tooltip title="Configure PlexTrac integration and manage report exports">
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          </div>
        )}

        {/* PlexTrac Reporting Content */}
        <PlexTracReporting
          customerEmail={customerEmail}
          isPraetorianUser={isPraetorianUser}
          showTitle={false}
        />
      </div>
    </div>
  );
};
